// components/GoogleDocsEditor.jsx - Embedded Google Docs editor
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

/**
 * GoogleDocsEditor - Embeds Google Docs editor in an iframe
 * Replaces TinyMCE with native Google Docs editing experience
 */
const GoogleDocsEditor = ({
  documentId,
  height = '800px',
  onDocumentReady = null,
  showToolbar = true
}) => {
  const { t } = useTranslation();
  const { isGoogleAuthenticated, ensureValidGoogleToken } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      if (!isGoogleAuthenticated) {
        setError(t('googleDocs.messages.loginRequired'));
        setIsLoading(false);
        return;
      }

      const isValid = await ensureValidGoogleToken();
      if (!isValid) {
        setError(t('dashboard.messages.sessionExpired'));
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
      if (onDocumentReady) {
        onDocumentReady(documentId);
      }
    };

    checkAuth();
  }, [isGoogleAuthenticated, documentId, onDocumentReady, ensureValidGoogleToken]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    toast.success(t('common.success'));
  };

  const handleIframeError = () => {
    setError(t('googleDocs.messages.loadFailed'));
    setIsLoading(false);
  };

  if (!documentId) {
    return (
      <div style={styles.errorContainer}>
        <p>No document ID provided</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <p>{error}</p>
      </div>
    );
  }

  // Construct Google Docs URL with options
  const docUrl = `https://docs.google.com/document/d/${documentId}/edit${!showToolbar ? '?rm=minimal' : ''}`;

  return (
    <div style={styles.container}>
      {isLoading && (
        <div style={styles.loadingOverlay}>
          <div style={styles.spinner} />
          <p>{t('common.loading')}</p>
        </div>
      )}

      <iframe
        src={docUrl}
        style={{
          ...styles.iframe,
          height: height
        }}
        title="Google Docs Editor"
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        allow="clipboard-read; clipboard-write"
      />
    </div>
  );
};

/**
 * GoogleDocsEditorSideBySide - Shows original and translation side by side
 */
export const GoogleDocsEditorSideBySide = ({
  originalDocumentId,
  translatedDocumentId,
  height = '800px'
}) => {
  return (
    <div style={styles.sideBySideContainer}>
      <div style={styles.editorHalf}>
        <GoogleDocsEditor
          documentId={originalDocumentId}
          height={height}
          showToolbar={false}
        />
      </div>

      <div style={styles.divider} />

      <div style={styles.editorHalf}>
        <GoogleDocsEditor
          documentId={translatedDocumentId}
          height={height}
          showToolbar={true}
        />
      </div>
    </div>
  );
};

const styles = {
  container: {
    position: 'relative',
    width: '100%',
    height: '100%',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
    backgroundColor: '#fff'
  },
  iframe: {
    width: '100%',
    border: 'none',
    display: 'block'
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f4f6',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  errorContainer: {
    padding: '40px',
    textAlign: 'center',
    backgroundColor: '#FFF4F2',
    border: '1px solid #FFCFC6',
    borderRadius: '8px',
    color: '#BE4338'
  },
  sideBySideContainer: {
    display: 'flex',
    gap: '0',
    width: '100%',
    height: '100%'
  },
  editorHalf: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0
  },
  editorTitle: {
    margin: 0,
    padding: '12px 16px',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151'
  },
  divider: {
    width: '1px',
    backgroundColor: '#e5e7eb'
  }
};

// Add spinner animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default GoogleDocsEditor;
